import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { renderMarkdown } from "@/lib/markdown";
import { rarityMeta, RARITIES } from "@/lib/itemMeta";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import ItemFrame from "@/components/ItemFrame";
import { CaseDropsPanel, DropsFromPanel } from "@/components/CaseDrops";
import { ChevronLeft, ChevronDown, Edit3, Image as ImageIcon, Package, Check, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function imageUrl(u) {
  if (!u) return null;
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/api/")) return BACKEND_URL + u;
  return u;
}

// Grouped type filter — "Armor" rolls up all wearable slots.
const TYPE_GROUPS = [
  { key: "Raw Material",       label: "Raw Materials",       members: ["Raw Material"] },
  { key: "Processed Material", label: "Processed Materials", members: ["Processed Material"] },
  { key: "Weapon",             label: "Weapons",             members: ["Weapon"] },
  { key: "Armor",              label: "Armor",               members: ["Helmet", "Chest", "Pants", "Boots", "Gloves"] },
  { key: "Consumable",         label: "Consumables",         members: ["Consumable"] },
  { key: "Case",               label: "Cases",               members: ["Case"] },
  { key: "Other",              label: "Other",               members: ["Other"] },
];

const RARITY_ORDER = ["Common", "Uncommon", "Rare", "Epic", "Legendary", "Mythic"];

const SORT_OPTIONS = [
  { key: "name-asc",    label: "Name (A → Z)" },
  { key: "name-desc",   label: "Name (Z → A)" },
  { key: "rarity-desc", label: "Rarity (Mythic → Common)" },
  { key: "rarity-asc",  label: "Rarity (Common → Mythic)" },
  { key: "newest",      label: "Newest first" },
  { key: "oldest",      label: "Oldest first" },
];

export default function ItemsList() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filterType, setFilterType] = useState("");   // TYPE_GROUPS.key or ""
  const [filterRarity, setFilterRarity] = useState(""); // RARITY key or ""
  const [sortKey, setSortKey] = useState("rarity-desc");
  const [loading, setLoading] = useState(true);

  // Fetch all items once. We filter & sort client-side so we can support
  // composite filters like "Armor" (5 underlying types).
  useEffect(() => {
    client.get("/items")
      .then(r => { setItems(r.data); setLoading(false); })
      .catch(() => { setItems([]); setLoading(false); });
  }, []);

  const filteredSorted = useMemo(() => {
    const group = TYPE_GROUPS.find(g => g.key === filterType);
    let list = items.filter(it => {
      if (group && !group.members.includes(it.type)) return false;
      if (filterRarity && it.rarity !== filterRarity) return false;
      return true;
    });
    const cmp = {
      "name-asc":    (a, b) => (a.name || "").localeCompare(b.name || ""),
      "name-desc":   (a, b) => (b.name || "").localeCompare(a.name || ""),
      "rarity-asc":  (a, b) => RARITY_ORDER.indexOf(a.rarity) - RARITY_ORDER.indexOf(b.rarity),
      "rarity-desc": (a, b) => RARITY_ORDER.indexOf(b.rarity) - RARITY_ORDER.indexOf(a.rarity),
      "newest":      (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
      "oldest":      (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
    }[sortKey];
    return cmp ? [...list].sort(cmp) : list;
  }, [items, filterType, filterRarity, sortKey]);

  const hasFilters = !!(filterType || filterRarity);
  const activeRarity = RARITIES.find(r => r.key === filterRarity);
  const activeType = TYPE_GROUPS.find(g => g.key === filterType);
  const activeSort = SORT_OPTIONS.find(s => s.key === sortKey);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12" data-testid="items-list-page">
      <div className="flex items-end justify-between gap-4 mb-7 md:mb-10">
        <div>
          <Link to="/wiki" className="inline-flex items-center gap-1 text-[10px] md:text-xs font-mono tracking-[0.2em] text-[var(--t-blue-glow)] uppercase hover:text-[var(--t-cyan)] mb-2" data-testid="items-back-to-wiki">
            <ChevronLeft className="w-3 h-3" /> Wiki
          </Link>
          <span className="font-mono text-[10px] md:text-xs tracking-[0.25em] md:tracking-[0.3em] text-[var(--t-blue-glow)] uppercase">// Items Archive</span>
          <h1 className="font-display text-3xl md:text-5xl mt-2 text-[var(--t-text)] uppercase leading-tight">Items</h1>
          <p className="text-sm text-[var(--t-text-dim)] mt-2">{filteredSorted.length} of {items.length} items · sorted by {activeSort?.label.toLowerCase()}</p>
        </div>
        {user?.is_admin && (
          <Link to="/admin?tab=items" className="btn-primary !py-2 !px-4 !text-xs" data-testid="items-manage-btn">
            <Package className="w-4 h-4" /> Manage
          </Link>
        )}
      </div>

      {/* Major filters */}
      <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="items-filters">
        <FilterButton
          label={hasFilters ? "Clear filters" : "All Items"}
          active={!hasFilters}
          onClick={() => { setFilterType(""); setFilterRarity(""); }}
          testid="filter-all-items"
          icon={hasFilters ? X : null}
        />

        <FilterDropdown
          label="Type"
          activeLabel={activeType?.label}
          testid="filter-type"
        >
          {({ close }) => (
            <DropdownList
              options={[{ key: "", label: "All Types" }, ...TYPE_GROUPS.map(g => ({ key: g.key, label: g.label }))]}
              value={filterType}
              onSelect={(v) => { setFilterType(v); close(); }}
              testidPrefix="ft"
            />
          )}
        </FilterDropdown>

        <FilterDropdown
          label="Rarity"
          activeLabel={activeRarity?.key}
          activeColor={activeRarity?.color}
          testid="filter-rarity"
        >
          {({ close }) => (
            <DropdownList
              options={[
                { key: "", label: "All Rarities" },
                ...RARITIES.map(r => ({ key: r.key, label: r.key, color: r.color })),
              ]}
              value={filterRarity}
              onSelect={(v) => { setFilterRarity(v); close(); }}
              testidPrefix="fr"
            />
          )}
        </FilterDropdown>

        <FilterDropdown
          label="Sort"
          activeLabel={activeSort?.label}
          testid="filter-sort"
        >
          {({ close }) => (
            <DropdownList
              options={SORT_OPTIONS}
              value={sortKey}
              onSelect={(v) => { setSortKey(v); close(); }}
              testidPrefix="fs"
            />
          )}
        </FilterDropdown>
      </div>

      <ItemsBody loading={loading} items={filteredSorted} canManage={!!user?.is_admin} />
    </div>
  );
}

function ItemsBody({ loading, items, canManage }) {
  if (loading) {
    return <div className="text-center py-16 font-mono text-xs tracking-[0.3em] text-[var(--t-blue-glow)] uppercase pulse-glow">// loading vault...</div>;
  }
  if (items.length === 0) {
    return (
      <div className="panel p-10 text-center" data-testid="items-empty">
        <Package className="w-12 h-12 text-[var(--t-text-muted)] mx-auto mb-3" />
        <h3 className="font-display text-lg text-[var(--t-cyan)] uppercase mb-2">Archive is growing</h3>
        <p className="text-sm text-[var(--t-text-dim)] mb-4">No items match the current filters yet. Check back soon — Citizens are cataloging every patch.</p>
        {canManage && <Link to="/admin?tab=items" className="btn-ghost !justify-center inline-flex">Add the first item</Link>}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="items-grid">
      {items.map(it => <ItemCard key={it.slug} item={it} />)}
    </div>
  );
}

function FilterButton({ label, active, onClick, testid, icon: Icon }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-[11px] md:text-xs font-mono tracking-[0.2em] uppercase border transition ${
        active
          ? "border-[var(--t-blue)] text-[var(--t-cyan)] bg-[rgba(58,160,255,0.08)]"
          : "border-[var(--t-border)] text-[var(--t-text-dim)] hover:text-[var(--t-text)] hover:border-[var(--t-border-hi)]"
      }`}
    >
      {Icon && <Icon className="w-3 h-3" />}
      {label}
    </button>
  );
}

function FilterDropdown({ label, activeLabel, activeColor, testid, children }) {
  const [open, setOpen] = useState(false);
  const isActive = !!activeLabel;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={`${testid}-trigger`}
          className={`inline-flex items-center gap-2 px-3 md:px-3.5 py-2 text-[11px] md:text-xs font-mono tracking-[0.18em] md:tracking-[0.2em] uppercase border transition max-w-full ${
            isActive
              ? "border-[var(--t-blue)] text-[var(--t-cyan)] bg-[rgba(58,160,255,0.08)]"
              : "border-[var(--t-border)] text-[var(--t-text-dim)] hover:text-[var(--t-text)] hover:border-[var(--t-border-hi)]"
          }`}
        >
          {activeColor && (
            <span
              className="inline-block w-2 h-2 rounded-full flex-shrink-0"
              style={{ background: activeColor, boxShadow: `0 0 8px ${activeColor}` }}
            />
          )}
          <span className="truncate max-w-[160px] md:max-w-none">
            {label}
            {activeLabel && (
              <span className="ml-1.5 normal-case tracking-normal text-[var(--t-text)]">
                : {activeLabel}
              </span>
            )}
          </span>
          <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-60 p-1 bg-[#050912] border border-[var(--t-border-hi)] text-[var(--t-text)] shadow-[0_0_30px_rgba(58,160,255,0.18)]"
        data-testid={`${testid}-menu`}
      >
        {typeof children === "function" ? children({ close: () => setOpen(false) }) : children}
      </PopoverContent>
    </Popover>
  );
}

function DropdownList({ options, value, onSelect, testidPrefix }) {
  return (
    <ul className="max-h-72 overflow-auto py-1" role="listbox">
      {options.map(opt => {
        const selected = opt.key === value;
        return (
          <li key={opt.key || "__all__"}>
            <button
              type="button"
              onClick={() => onSelect(opt.key)}
              data-testid={`${testidPrefix}-${(opt.key || "all").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs font-mono tracking-[0.1em] uppercase transition ${
                selected
                  ? "bg-[rgba(58,160,255,0.12)] text-[var(--t-cyan)]"
                  : "text-[var(--t-text-dim)] hover:bg-[rgba(58,160,255,0.06)] hover:text-[var(--t-text)]"
              }`}
            >
              {opt.color !== undefined ? (
                <span
                  className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                  style={{
                    background: opt.color || "transparent",
                    boxShadow: opt.color ? `0 0 8px ${opt.color}` : undefined,
                    border: opt.color ? "none" : "1px dashed var(--t-text-muted)",
                  }}
                />
              ) : null}
              <span className="flex-1">{opt.label}</span>
              {selected && <Check className="w-3.5 h-3.5 text-[var(--t-cyan)] flex-shrink-0" />}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function ItemCard({ item }) {
  const r = rarityMeta(item.rarity);
  const img = imageUrl(item.image_url);
  return (
    <Link
      to={`/wiki/items/${item.slug}`}
      className="group block panel hover:border-[var(--t-blue)] transition overflow-hidden"
      data-testid={`item-card-${item.slug}`}
      style={{ "--rarity": r.color }}
    >
      <ItemFrame
        rarity={item.rarity}
        image={img}
        alt={item.name}
        imgClassName="group-hover:scale-105 transition-transform duration-500"
      >
        <span className={`absolute top-2 left-2 inline-flex items-center px-2 py-0.5 text-[9px] font-mono tracking-[0.15em] uppercase border ${r.chip}`}>
          {item.rarity}
        </span>
        <span className="absolute top-2 right-2 inline-flex items-center px-2 py-0.5 text-[9px] font-mono tracking-[0.15em] uppercase bg-[#050912]/80 text-[var(--t-steel)] border border-[var(--t-border)]">
          {item.type}
        </span>
      </ItemFrame>
      <div className="p-3 border-t" style={{ borderColor: r.color, opacity: 0.95 }}>
        <h3 className="font-display text-sm md:text-base uppercase leading-tight truncate" style={{ color: r.color, textShadow: `0 0 12px ${r.glow}` }}>{item.name}</h3>
        {item.stats && Object.keys(item.stats).length > 0 && (
          <div className="mt-1 text-[10px] font-mono text-[var(--t-text-dim)] tracking-[0.1em] uppercase truncate">
            {Object.entries(item.stats).slice(0, 2).map(([k, v]) => `${k}: ${v}`).join(" · ")}
          </div>
        )}
      </div>
    </Link>
  );
}

export function ItemDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await client.get(`/items/${slug}`);
        if (!cancelled) { setItem(r.data); setNotFound(false); }
      } catch (e) {
        if (!cancelled && e?.response?.status === 404) { setItem(null); setNotFound(true); }
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 md:py-12" data-testid="item-detail-page">
      <Link to="/wiki/items" className="inline-flex items-center gap-1 text-[10px] md:text-xs font-mono tracking-[0.2em] text-[var(--t-blue-glow)] uppercase hover:text-[var(--t-cyan)] mb-6" data-testid="item-back">
        <ChevronLeft className="w-3 h-3" /> All Items
      </Link>

      {notFound && (
        <div className="text-center py-20">
          <h1 className="font-display text-3xl text-[var(--t-cyan)] uppercase mb-2">Item Not Found</h1>
          <p className="text-sm text-[var(--t-text-dim)]">This entry is not in the vault.</p>
        </div>
      )}

      {item && <ItemView item={item} canEdit={user?.is_admin} onEdit={() => navigate(`/admin?tab=items&edit=${item.slug}`)} />}
    </div>
  );
}

function ItemView({ item, canEdit, onEdit }) {
  const r = rarityMeta(item.rarity);
  const img = imageUrl(item.image_url);
  return (
    <div className="grid md:grid-cols-[1fr_1.2fr] gap-8 lg:gap-12" data-testid="item-view">
      <div className="relative">
        <ItemFrame
          rarity={item.rarity}
          image={img}
          alt={item.name}
          className="panel"
        />
      </div>

      <div>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className={`inline-flex items-center px-2.5 py-1 text-[10px] font-mono tracking-[0.2em] uppercase border ${r.chip}`}>{item.rarity}</span>
          <span className="inline-flex items-center px-2.5 py-1 text-[10px] font-mono tracking-[0.2em] uppercase bg-[#050912] text-[var(--t-steel)] border border-[var(--t-border)]">{item.type}</span>
          {canEdit && (
            <button onClick={onEdit} className="ml-auto btn-ghost !py-1.5 !px-3 !text-[10px]" data-testid="item-edit-btn">
              <Edit3 className="w-3 h-3" /> Edit
            </button>
          )}
        </div>

        <h1 className="font-display text-3xl md:text-5xl uppercase leading-tight mb-4" style={{ color: r.color, textShadow: `0 0 24px ${r.glow}` }}>{item.name}</h1>

        {item.description && (
          <div className="prose-t mb-6" dangerouslySetInnerHTML={{ __html: renderMarkdown(item.description) }} />
        )}

        {/* Case-only: drops grid + open simulator */}
        {item.type === "Case" && <CaseDropsPanel item={item} />}

        {/* Non-case: reverse linking to cases that drop this item */}
        {item.type !== "Case" && <DropsFromPanel slug={item.slug} />}

        {item.stats && Object.keys(item.stats).length > 0 && (
          <div className="panel p-5 mb-6" data-testid="item-stats">
            <div className="font-mono text-xs tracking-[0.25em] text-[var(--t-blue-glow)] uppercase mb-3">// Stats</div>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2">
              {Object.entries(item.stats).map(([k, v]) => (
                <div key={k} className="flex justify-between items-baseline border-b border-[var(--t-border)] py-1.5">
                  <dt className="text-xs font-mono text-[var(--t-text-dim)] tracking-[0.1em] uppercase">{k}</dt>
                  <dd className="font-display text-sm" style={{ color: r.color }}>{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {item.production_chain && item.production_chain.length > 0 && (
          <div className="panel p-5 mb-6" data-testid="item-production-chain">
            <div className="font-mono text-xs tracking-[0.25em] text-[var(--t-blue-glow)] uppercase mb-3">// Production Chain</div>
            <div className="flex flex-wrap items-center gap-2">
              {item.production_chain.map((slug, i) => (
                <span key={slug} className="inline-flex items-center gap-2">
                  {i > 0 && <span className="font-mono text-[var(--t-text-muted)] text-xs">+</span>}
                  <Link
                    to={`/wiki/items/${slug}`}
                    className="inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--t-border-glow)] bg-[rgba(58,160,255,0.06)] hover:border-[var(--t-blue)] hover:bg-[rgba(58,160,255,0.12)] transition group"
                    data-testid={`chain-link-${slug}`}
                  >
                    <span className="font-display text-sm uppercase tracking-[0.06em] text-[var(--t-cyan)] group-hover:text-[var(--t-blue-glow)]">{slug.replace(/-/g, " ")}</span>
                  </Link>
                </span>
              ))}
              <span className="font-mono text-[var(--t-text-muted)] text-xs">→</span>
              <span className="font-display text-sm uppercase tracking-[0.06em]" style={{ color: r.color }}>{item.name}</span>
            </div>
          </div>
        )}

        {item.source && (
          <div className="panel p-5" data-testid="item-source">
            <div className="font-mono text-xs tracking-[0.25em] text-[var(--t-blue-glow)] uppercase mb-3">// Source</div>
            <div className="prose-t" dangerouslySetInnerHTML={{ __html: renderMarkdown(item.source) }} />
          </div>
        )}
      </div>
    </div>
  );
}
